import { useCallback, useState, ChangeEvent, useEffect } from 'react';
import dayjs from 'dayjs';
import { UseFormWatch, UseFormSetValue, set } from 'react-hook-form';
import styled, { StyledComponent } from 'styled-components';
import { Stack, StackProps } from 'components/Stack';
import { Text } from 'components/Typography';
import { Button } from 'components/Button';
import { TLocalForm } from './AutoMessagePart';
import { useIsDifferenceStore } from '../common/MessagePromptStore';

import { useSingleAutoMessage } from './AutoStore';
import { AlertModal } from 'components/Modal/AlertModal';

type Props = {
  watch: UseFormWatch<TLocalForm>;
  setValue: UseFormSetValue<TLocalForm>;
};
const AutoMessageComponent = ({ watch, setValue }: Props) => {
  const { singleMessage, setSingleMessage } = useSingleAutoMessage();
  const [isOpenTimeError, setIsOpenTimeError] = useState(false);
  const { setIsDifference } = useIsDifferenceStore();

  useEffect(() => {
    if (singleMessage) {
      // 토글 버튼 처리
      setValue('isChecked', singleMessage.disabledSendTime);
      // 시간처리
      const startTime = dayjs(singleMessage.sendStartTime === '' ? '00:00' : singleMessage.sendStartTime, 'HH:mm');
      const endTime = dayjs(singleMessage.sendEndTime === '' ? '24:00' : singleMessage.sendEndTime, 'HH:mm');

      setValue('sendStartHour', startTime.hour());
      setValue('sendStartMinute', startTime.minute());
      setValue('sendEndHour', endTime.hour());
      setValue('sendEndMinute', endTime.minute());

      // 초기 익일 처리
      if (startTime?.isBefore(endTime)) {
        setIsOpenTimeError(false);
      } else {
        setIsOpenTimeError(true);
      }
    }
  }, [singleMessage]);

  const onChangeTime = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      let { name, value } = event.target;
      let startTime, endTime;
      // 시간 min/max 제한 처리
      if (name.includes('Hour')) {
        if (Number(value) < 0) {
          value = '0';
        }
        if (Number(value) > 23) {
          value = '23';
        }
      }
      // 분 min/max 제한 처리
      if (name.includes('Minute')) {
        if (Number(value) < 0) {
          value = '0';
        }
        if (Number(value) > 59) {
          value = '59';
        }
      }
      switch (name) {
        case 'sendStartHour':
          name = 'sendStartHour';
          startTime = dayjs().hour(Number(value)).minute(watch('sendStartMinute'));
          endTime = dayjs().hour(watch('sendEndHour')).minute(watch('sendEndMinute'));
          break;
        case 'sendStartMinute':
          name = 'sendStartMinute';
          startTime = dayjs().hour(watch('sendStartHour')).minute(Number(value));
          endTime = dayjs().hour(watch('sendEndHour')).minute(watch('sendEndMinute'));
          break;
        case 'sendEndHour':
          name = 'sendEndHour';
          startTime = dayjs().hour(watch('sendStartHour')).minute(watch('sendStartMinute'));
          endTime = dayjs().hour(Number(value)).minute(watch('sendEndMinute'));
          break;
        case 'sendEndMinute':
          name = 'sendEndMinute';
          startTime = dayjs().hour(watch('sendStartHour')).minute(watch('sendStartMinute'));
          endTime = dayjs().hour(watch('sendEndHour')).minute(Number(value));
          break;
        default:
          break;
      }
      // 비교하기
      if (startTime?.isBefore(endTime)) {
        // //@ts-expect-error
        // setValue(name, Number(value));
        setIsOpenTimeError(false);
      } else {
        setIsOpenTimeError(true);
      }
      //@ts-expect-error
      setValue(name, Number(value));
      // 데이터 비교
      if (!!singleMessage) {
        const startTime =
          watch('sendStartHour').toString().padStart(2, '0') +
          ':' +
          watch('sendStartMinute').toString().padStart(2, '0');
        const endTime =
          watch('sendEndHour').toString().padStart(2, '0') + ':' + watch('sendEndMinute').toString().padStart(2, '0');
        const singleMEssageStartTime = dayjs(singleMessage.sendStartTime, 'HH:mm:ss').format('HH:mm');
        const singleMEssageEndTime = dayjs(singleMessage.sendEndTime, 'HH:mm:ss').format('HH:mm');
        const isSameStartTime = startTime === singleMEssageStartTime;
        const isSameEndTime = endTime === singleMEssageEndTime;
        const isSameIsChecked = watch('isChecked') === singleMessage.disabledSendTime;
        if (isSameStartTime && isSameEndTime && isSameIsChecked) {
          setIsDifference(false);
        } else {
          setIsDifference(true);
        }
      }
    },
    [singleMessage]
  );

  const onChangeToggle = useCallback(() => {
    if (!!singleMessage) {
      const isSameIsChecked = watch('isChecked') === singleMessage.disabledSendTime;
      if (isSameIsChecked) {
        setIsDifference(true);
      } else {
        setIsDifference(false);
      }
    }
    setValue('isChecked', !watch('isChecked'));
  }, [singleMessage]);

  return (
    <>
      <Div>
        <FirstDiv>
          <FirstDivTitle>메시지 내용</FirstDivTitle>
          <FirstDivSubTitle>카카오 알림톡으로 발송되며, 카카오톡이 없는 수신인은 LMS로 수신합니다.</FirstDivSubTitle>
        </FirstDiv>
        <SecondDiv>
          <SecondDivContent>{singleMessage?.contents.replaceAll('<br>', '\n')}</SecondDivContent>
        </SecondDiv>

        <SetTimeDiv>
          <SetTimeFirstDiv>
            <SetTimeFirstDivToggleDesc>
              <ToggleBoxContainer>
                <StyledCheckbox checked={watch('isChecked')} onChange={onChangeToggle} />
              </ToggleBoxContainer>
              <SetTimeFirstDivToggleTitle>발송예외 시간 설정</SetTimeFirstDivToggleTitle>
            </SetTimeFirstDivToggleDesc>
            <SetTimeFirstDivToggleSubTitle>
              설정한 시간에는 메시지 발송이 되지 않으며, 발송 이력 조회에서 내역만 조회할 수 있습니다.
              <br />
              시작 시간과 종료 시간 사이에 자정이 포함되는 경우 종료 시간이 익일까지로 설정됩니다.
            </SetTimeFirstDivToggleSubTitle>
          </SetTimeFirstDiv>
          <SetTimeDivSecondDivWrapper>
            <SetTimeDivSecondDiv>
              <SetTimeDivSecondDiv>
                {/* <TimeContaniner color={isChecked ? 'white' : '#f9f9f9'}> */}
                <TimeContaniner color={watch('isChecked') ? 'white' : '#f9f9f9'}>
                  <TimeInput
                    type="number"
                    min={0}
                    max={23}
                    name="sendStartHour"
                    value={
                      watch('sendStartHour').toString().length === 1
                        ? `0${watch('sendStartHour')}`
                        : watch('sendStartHour').toString()
                    }
                    onChange={onChangeTime}
                    disabled={!watch('isChecked')}
                  />
                  <TimeText>시</TimeText>
                </TimeContaniner>
                <TimeContaniner color={watch('isChecked') ? 'white' : '#f9f9f9'}>
                  <TimeInput
                    type="number"
                    min={0}
                    max={59}
                    name="sendStartMinute"
                    value={
                      watch('sendStartMinute').toString().length === 1
                        ? `0${watch('sendStartMinute')}`
                        : watch('sendStartMinute').toString()
                    }
                    onChange={onChangeTime}
                    disabled={!watch('isChecked')}
                  />
                  <TimeText>분</TimeText>
                </TimeContaniner>
              </SetTimeDivSecondDiv>
              ~
              <SetTimeDivSecondDiv>
                <TimeContaniner color={watch('isChecked') ? 'white' : '#f9f9f9'}>
                  <TimeInput
                    type="number"
                    min={0}
                    max={23}
                    name="sendEndHour"
                    value={
                      watch('sendEndHour').toString().length === 1
                        ? `0${watch('sendEndHour')}`
                        : watch('sendEndHour').toString()
                    }
                    onChange={onChangeTime}
                    disabled={!watch('isChecked')}
                  />
                  <TimeText>시</TimeText>
                </TimeContaniner>
                <TimeContaniner color={watch('isChecked') ? 'white' : '#f9f9f9'}>
                  <TimeInput
                    type="number"
                    min={0}
                    max={59}
                    name="sendEndMinute"
                    value={
                      watch('sendEndMinute').toString().length === 1
                        ? `0${watch('sendEndMinute')}`
                        : watch('sendEndMinute').toString()
                    }
                    onChange={onChangeTime}
                    disabled={!watch('isChecked')}
                  />
                  <TimeText>분</TimeText>
                </TimeContaniner>
              </SetTimeDivSecondDiv>
            </SetTimeDivSecondDiv>
            {isOpenTimeError && <SetTimeDivSecondDivIcon>익일</SetTimeDivSecondDivIcon>}
          </SetTimeDivSecondDivWrapper>
        </SetTimeDiv>
      </Div>
      {/* <AlertModal
        type="WARNING"
        isOpen={isOpenTimeError}
        messages={
          <Stack sx={{ gap: '8px' }}>
            <Text styleName="body2" color="RG02">
              종료 시간이 시작 시간보다 빠를 수 없습니다.
            </Text>
            <Text styleName="body2" color="RG02">
              다시 입력해 주세요.
            </Text>
          </Stack>
        }
        buttonGroup={
          <Button
            variant="tertiary"
            h={40}
            sx={{ width: '100%', marginTop: '40px' }}
            onClick={() => {
              setIsOpenTimeError(false);
            }}
          >
            <Text styleName="body1" color="RG03">
              확인
            </Text>
          </Button>
        }
      /> */}
    </>
  );
};

export default AutoMessageComponent;

const Div = styled.div`
  display: flex;
  padding: 0px 20px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
`;
const FirstDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
const FirstDivTitle = styled.div`
  color: var(--RG02, #25282f);
  font-feature-settings: 'liga' off, 'clig' off;

  /* body16B */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 150% */
  letter-spacing: -0.32px;
`;
const FirstDivSubTitle = styled.div`
  color: var(--GrayScale-gray800, #565b69);
  font-feature-settings: 'liga' off, 'clig' off;

  /* body14M */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 171.429% */
  letter-spacing: -0.28px;
`;
const SecondDiv = styled.div`
  //readonly로 처리할것
  display: flex;
  padding: 8px 10px;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;

  border-radius: 8px;
  border: 1px solid var(--RG06, #d8ddea);
  background: #f9f9f9;

  color: var(--semantic-color-text-text-default, #1c1d23);
  font-feature-settings: 'liga' off, 'clig' off;

  /* body14M */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 171.429% */
  letter-spacing: -0.28px;
`;
const SecondDivContent = styled.div`
  flex: 1 0 0;
  color: #6e727c;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.02em;
  text-align: left;
  white-space: pre-line;
`;

const SetTimeDiv = styled.div`
  display: flex;
  padding: 0px 20px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;
const SetTimeFirstDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;
const SetTimeFirstDivToggleDesc = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const SetTimeFirstDivToggleTitle = styled.div`
  color: var(--RG02, #25282f);
  font-feature-settings: 'liga' off, 'clig' off;

  /* body16B */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 150% */
  letter-spacing: -0.32px;
`;
const SetTimeFirstDivToggleSubTitle = styled.div`
  color: var(--GrayScale-gray800, #565b69);
  font-feature-settings: 'liga' off, 'clig' off;

  /* body14M */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 171.429% */
  letter-spacing: -0.28px;
`;

const ToggleBoxContainer = styled.div`
  width: 32px;
  height: 20px;
  flex-shrink: 0;
`;

const StyledCheckbox: StyledComponent<
  'input',
  any,
  {
    type: 'checkbox';
  },
  'type'
> = styled.input.attrs({ type: 'checkbox' })`
  appearance: none;
  position: relative;
  border: max(2px, 0.1em) solid #e2e4eb;
  border-radius: 1.25em;
  width: 32px;
  height: 20px;
  background-color: #e2e4eb;
  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    width: 17px;
    height: 17px;
    border-radius: 50%;
    transform: scale(0.8);
    background-color: #ffffff;
    transition: left 250ms linear;
  }

  &:checked {
    background-color: #1c48cd;
    border-color: #1c48cd;
  }

  &:checked::before {
    background-color: #ffffff;
    left: 11px;
  }
`;

const SetTimeDivSecondDivWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SetTimeDivSecondDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SetTimeDivSecondDivIcon = styled.div`
  display: flex;
  height: 20px;
  padding: 0px 6px;
  justify-content: center;
  align-items: center;
  gap: 4px;

  border-radius: 4px;
  background: var(--Primary-Blue200, #eaf0ff);

  color: var(--Primary-Blue700, #2e66f6);

  /* Caption/CaptionM12 */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 21px; /* 175% */
  letter-spacing: -0.24px;
`;

const TimeContaniner = styled.div`
  display: flex;
  width: 68px;
  height: 40px;
  padding: 8px 10px 8px 10px;
  align-items: center;
  gap: 10px;

  border-radius: 8px;
  border: 1px solid var(--RG06, #d8ddea);
  background: var(--RG00, #fff);
  background-color: ${props => props.color};
`;

const TimeInput = styled.input`
  background-color: inherit;
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  width: 25px;

  color: #6e727c;

  /* subheadline2 */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px; /* 157.143% */
  letter-spacing: -0.28px;

  /* padding: 8px 10px 8px 10px; */
`;

const TimeText = styled.div`
  color: #6e727c;
  width: 13px;
  z-index: 1;
  position: relative;

  /* subheadline2 */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px; /* 157.143% */
  letter-spacing: -0.28px;
`;
